/**
 * InspectionRatingsPanel - Dashboard panel summarizing inspection quality.
 *
 * Answers the two questions operations actually asks of the inspection
 * program: how are sites scoring, and is anything on fire right now.
 *
 *   - A tile per rating tier (Good / Needs Attention / Critical) plus the
 *     inspections nobody has scored yet, so the totals always reconcile.
 *   - A prominent callout when open inspections carry unresolved critical
 *     findings, since those escalate to the branch manager if they sit.
 *
 * Reads the rating rollup the API maintains on write, so nothing here has to
 * walk a checklist. Tier colors and wording come from utils/inspectionRating,
 * the same source the field picker and badges use.
 */
import React from 'react';
import { INSPECTION_RATINGS } from '../../../utils/inspectionRating';
import type { DashboardInspectionCounts } from '../../../services/dashboardService';

export interface InspectionRatingsPanelProps {
	/** The inspections block from GET /dashboard/metrics. */
	counts: DashboardInspectionCounts;
	/** Called when a tier tile is activated, e.g. to filter the list. */
	onSelectRating?: (rating: string) => void;
	/** Called when the critical callout is activated. */
	onSelectCritical?: () => void;
	/** Optional extra classes merged onto the root. */
	className?: string;
}

export const InspectionRatingsPanel: React.FC<InspectionRatingsPanelProps> = ({
	counts,
	onSelectRating,
	onSelectCritical,
	className = '',
}) => {
	const byRating = counts.byRating ?? {};
	const unrated = Number(byRating.unrated ?? 0);
	const openCritical = Number(counts.openCritical ?? 0);

	return (
		<section className={`inspection-ratings-panel bg-white rounded-lg shadow p-16px tablet:p-24px ${className}`} data-ref="organism-inspection-ratings-panel">
			<div className="inspection-ratings-panel__header flex items-center justify-between gap-8px mb-16px">
				<h2 className="text-18px font-heading uppercase tracking-wide text-brand-heading">
					Inspection Ratings
				</h2>
				<span className="text-12px font-sans text-brand-text-muted">
					{counts.total} inspection{counts.total === 1 ? '' : 's'}
				</span>
			</div>

			{/* --- Open critical findings ---
			    Rendered first and loudly: these escalate to the branch manager
			    if they are still open after the escalation window. */}
			{openCritical > 0 && (
				<button
					type="button"
					onClick={onSelectCritical}
					disabled={!onSelectCritical}
					className="inspection-ratings-panel__critical w-full flex items-start gap-10px text-left mb-16px px-12px py-10px rounded-md border border-red-300 bg-red-50 min-h-44px transition-colors hover:bg-red-100 disabled:cursor-default disabled:hover:bg-red-50"
				>
					<svg className="w-18px h-18px text-red-600 flex-shrink-0 mt-2px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
					</svg>
					<span>
						<span className="block text-14px font-sans font-semibold text-red-800">
							{openCritical} open inspection{openCritical === 1 ? '' : 's'} with critical findings
						</span>
						<span className="block text-12px font-sans text-red-700">
							Unresolved critical items escalate to the branch manager automatically.
						</span>
					</span>
				</button>
			)}

			{/* --- Tier tiles --- */}
			<div className="inspection-ratings-panel__tiers grid grid-cols-2 tablet:grid-cols-4 gap-12px">
				{INSPECTION_RATINGS.map((meta) => {
					const value = Number(byRating[meta.value] ?? 0);
					return (
						<button
							key={meta.value}
							type="button"
							onClick={() => onSelectRating?.(meta.value)}
							disabled={!onSelectRating}
							title={meta.action}
							className={`inspection-ratings-panel__tier inspection-ratings-panel__tier--${meta.value} flex flex-col items-start px-12px py-10px rounded-md border text-left min-h-44px transition-opacity hover:opacity-80 disabled:cursor-default disabled:hover:opacity-100 ${meta.badgeClasses}`}
						>
							<span className="text-22px tablet:text-28px font-sans font-semibold leading-tight tabular-nums">
								{value}
							</span>
							<span className="text-11px font-sans font-semibold uppercase tracking-wide mt-2px">
								{meta.label}
							</span>
						</button>
					);
				})}

				{/* Unrated is a real state — an inspection in progress — so it is
				    shown rather than hidden, and the tiles then reconcile to the
				    total above. */}
				<button
					type="button"
					onClick={() => onSelectRating?.('unrated')}
					disabled={!onSelectRating}
					title="No items rated yet"
					className="inspection-ratings-panel__tier inspection-ratings-panel__tier--unrated flex flex-col items-start px-12px py-10px rounded-md border border-gray-300 bg-gray-100 text-gray-700 text-left min-h-44px transition-opacity hover:opacity-80 disabled:cursor-default disabled:hover:opacity-100"
				>
					<span className="text-22px tablet:text-28px font-sans font-semibold leading-tight tabular-nums">
						{unrated}
					</span>
					<span className="text-11px font-sans font-semibold uppercase tracking-wide mt-2px">
						Not rated
					</span>
				</button>
			</div>
		</section>
	);
};

export default InspectionRatingsPanel;
